import { Button } from 'components/atoms';
import { useStoreState } from 'providers/StoreStateContext';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiList, PiUserCircle, PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetAccounts } from 'services/account';
import { INavbarItem } from 'types/navbar/navbar.interface';
import { getParentPathname } from 'utils/navbar/getParentPathname';
import { navbarItems } from 'utils/navbar/navbarItems';

import { useGetUnreadThreadsCount } from 'services/chat';
import { useGetClientModuleConfiguration } from 'services/configuration';
import './NavbarBurger.scss';

const getMainCategoryIdFromNavbarItem = (item: INavbarItem): string => {
  return item.path as string;
};

const getDefaultSubroute = (item: INavbarItem) => {
  const firstChild = item.children?.[0];
  if (!firstChild) return `/${item.path}`;
  return `/${firstChild.path}`;
};

const NavbarBurger = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const configurationModule = useGetClientModuleConfiguration();

  const { tokenData, lastSubrouteByCategory } = useStoreState();
  const accounts = useGetAccounts();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {}, [pathname]);

  const isPageActive = (name: string): boolean =>
    getParentPathname(pathname) === name || pathname === `/${name}`;

  const navigateByGroup = (item: INavbarItem) => {
    const mainCategory = getMainCategoryIdFromNavbarItem(item);
    const last = lastSubrouteByCategory?.[mainCategory];
    const defaultPath = getDefaultSubroute(item);
    const target = last ?? defaultPath;

    navigate(target);
    setIsOpen(false);
  };

  const navigateToChild = (child: INavbarItem) => {
    navigate(`/${child.path}`);
    setIsOpen(false);
  };

  const openDrawer = () => setIsOpen(true);
  const closeDrawer = () => setIsOpen(false);

  const currentAccount = accounts.data?.find((a) => a.id === tokenData?.CurrentAccount);
  const currentLabelName = currentAccount?.labelName ?? '';

  const mobileGroups = navbarItems.filter((i) => !i.hidden || i.path === 'settings');

  const unreadCount = useGetUnreadThreadsCount();

  return (
    <div className="navbar-burger">
      <div className="navbar-burger__top">
        <div className="navbar-burger__top-logo">
          <img
            src={
              configurationModule.data?.currentLogoPath ??
              `${process.env.PUBLIC_URL}/icons/logo.svg`
            }
            alt=""
          />
        </div>

        <Button isClear onlyIcon onClick={openDrawer}>
          <Button.Icon icon={<PiList size={22} />} />
          {!!unreadCount.data && <Button.Label>{unreadCount.data}</Button.Label>}
        </Button>
      </div>

      <div
        className={`navbar-burger__backdrop ${isOpen ? 'navbar-burger__backdrop--open' : ''}`}
        onClick={closeDrawer}
      />

      <aside className={`navbar-burger__panel ${isOpen ? 'navbar-burger__panel--open' : ''}`}>
        <div className="navbar-burger__panel-header">
          <Button
            isClear
            onlyIcon
            onClick={() => {
              // onClickGroup(navbarItems.find((item) => item.path === 'settings'));
              navigate(`/label`);
              closeDrawer();
            }}
          >
            <Button.Icon icon={<PiUserCircle />} />
            <Button.Text
              text={accounts.data?.find((a) => a.id === tokenData?.CurrentAccount)?.labelName ?? ''}
            />
          </Button>
          <Button isClear onlyIcon onClick={closeDrawer}>
            <Button.Icon icon={<PiX size={22} />} />
          </Button>
        </div>

        <div className="navbar-burger__sections">
          {mobileGroups.map((group) => (
            <div key={group.path} className="navbar-burger__section">
              <div className="navbar-burger__section-header">
                <span className="navbar-burger__section-title">
                  {t(`navbar.${group.name ?? group.path}`)}
                </span>
              </div>

              <div className="navbar-burger__section-links">
                {group.children?.length ? (
                  group.children.map((child) => (
                    <Button
                      key={child.path}
                      isClear
                      buttonStyle={{ justifyContent: 'start' }}
                      className="navbar-burger__link-btn"
                      fullWidth
                      isActive={isPageActive(child.path)}
                      onClick={() => navigateToChild(child)}
                    >
                      <Button.Icon
                        icon={
                          isPageActive(child.path) && child.fillIcon ? (
                            <child.fillIcon />
                          ) : (
                            <child.icon />
                          )
                        }
                      />
                      <Button.Text text={t(`navbar.${child.name ?? child.path}`)} />
                      {!!unreadCount.data && child.path === 'support' && (
                        <Button.Label>{unreadCount.data}</Button.Label>
                      )}
                    </Button>
                  ))
                ) : (
                  <Button
                    isClear
                    buttonStyle={{ justifyContent: 'start' }}
                    className="navbar-burger__link-btn"
                    isActive={isPageActive(group.path)}
                    fullWidth
                    onClick={() => navigateByGroup(group)}
                  >
                    <Button.Icon
                      icon={
                        isPageActive(group.path) && group.fillIcon ? (
                          <group.fillIcon />
                        ) : (
                          <group.icon />
                        )
                      }
                    />
                    <Button.Text text={t(`navbar.${group.name ?? group.path}`)} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
};

export default NavbarBurger;
